import { inferJLinkListSchema } from './link';
import { jObjectBase, jObjectLittle, jObjectLittleSchema } from './objectBase';
import { jUserLittle, jUserLittleSchema } from './user';
import { z, ZodType } from 'zod';

export const inferBookmarkRawListSchema = <
    T extends ZodType<jObjectBase> = ZodType<jObjectLittle> | ZodType<jUserLittle>,
>(
    type?: T,
) => {
    return inferJLinkListSchema(type ?? jObjectLittleSchema.or(jUserLittleSchema)).extend({
        bookmarkUrl: z.string().optional().nullable(),
        bookmarkOrder: z.number().optional(),
        bookmarkLabel: z.string().optional(),
        bookmarkMeta: z.string().optional().nullable(),
        url: z.string().optional().nullable(),
        type: z.literal('bookmarkLink'),
    });
};
export type BookmarkRawList<T extends jObjectBase = jObjectLittle | jUserLittle> = z.infer<
    ReturnType<typeof inferBookmarkRawListSchema<ZodType<T>>>
>;

export const inferBookmarkListSchema = <T extends ZodType<jObjectBase> = ZodType<jObjectLittle>>(type?: T) => {
    return z.object({
        id: z.number(),
        mainType: z.string(),
        type: z.string(),
        uri: z.string(),
        _values: z
            .object({
                dateCreation: z.string(),
                dateModified: z.string(),
            })
            .optional(),
        srcId: z.number(),
        srcType: z.string(),
        targetId: z.number(),
        targetType: z.string().nullable(),
        value: z.string().nullable(),
        _cssColor: z.string(),
        _cssClass: z.string(),
        _src: (type ?? jObjectLittleSchema).optional(),
        _target: (type ?? jObjectLittleSchema).optional(),
        bookmarkUrl: z.string().optional().nullable(),
        bookmarkOrder: z.string().optional(),
        bookmarkLabel: z.string().optional(),
        bookmarkMeta: z.string().optional().nullable(),
        url: z.string().optional().nullable(),
    });
};
export type BookmarkList<T extends jObjectBase = jObjectLittle> = z.infer<
    ReturnType<typeof inferBookmarkListSchema<ZodType<T>>>
>;

export enum BookmarkType {
    RAW_LIST = 'raw-list',
    LIST = 'list',
}

export const bookmarkLinkSchema = z.object({
    targetId: z.number(),
    targetType: z.string(),
    format: z
        .nativeEnum(BookmarkType)
        .transform((v) => `${v}` as const)
        .optional(),
});
export type BookmarkLink = z.infer<typeof bookmarkLinkSchema>;

export const bookmarkTypesSchema = z.object({
    [BookmarkType.RAW_LIST]: inferBookmarkRawListSchema(),
    [BookmarkType.LIST]: inferBookmarkListSchema(),
});
export type BookmarkTypes = z.infer<typeof bookmarkTypesSchema>;

export const bookmarkCustomSchema = z.object({
    bookmarkLabel: inferBookmarkRawListSchema(jObjectLittleSchema).shape.bookmarkLabel,
    bookmarkMeta: inferBookmarkRawListSchema(jObjectLittleSchema).shape.bookmarkMeta,
    bookmarkUrl: inferBookmarkRawListSchema(jObjectLittleSchema).shape.bookmarkUrl,
});
export type BookmarkCustom = z.infer<typeof bookmarkCustomSchema>;
